// PrepChat Service
// =================
// Lightweight text chat service for Written Preparation.
// Anna helps students formulate understanding through hybrid Socratic approach.
// Parses <note_update> tags from Anna's responses for note suggestions.

import { callClaudeRoute } from './claudeApi';
import i18n from '../i18n';

/** A single chat message */
export interface PrepChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  /** Parsed note update suggestion, if present */
  noteUpdate?: NoteUpdateSuggestion;
  /** Whether Anna marked a concept as qualified */
  conceptQualified?: boolean;
}

/** A note update suggestion parsed from Anna's response */
export interface NoteUpdateSuggestion {
  content: string;
  changeSummary: string;
}

/** Context passed to the service on creation */
export interface PrepChatContext {
  topicTitle: string;
  subtopics: Array<{ title: string }>;
  language: string;
}

/**
 * Parse <note_update> JSON from Anna's response.
 * Returns the suggestion if found, null otherwise.
 */
export function parseNoteUpdate(text: string): NoteUpdateSuggestion | null {
  const match = text.match(/<note_update>([\s\S]*?)<\/note_update>/i);
  if (!match) return null;

  try {
    const parsed = JSON.parse(match[1].trim());
    if (parsed.content && parsed.changeSummary) {
      return { content: parsed.content, changeSummary: parsed.changeSummary };
    }
  } catch {
    console.warn('[PrepChat] Failed to parse note_update JSON');
  }
  return null;
}

/**
 * Strip <note_update> tags from display text so the card shows cleanly
 */
export function stripNoteUpdateTags(text: string): string {
  return text.replace(/<note_update>[\s\S]*?<\/note_update>/gi, '').trim();
}

/**
 * Check if Anna marked a concept as qualified in her response.
 */
export function parseConceptQualified(text: string): boolean {
  return /<concept_qualified\s*\/?>/i.test(text);
}

/**
 * Strip <concept_qualified> tags from display text
 */
export function stripConceptQualifiedTags(text: string): string {
  return text.replace(/<concept_qualified\s*\/?>/gi, '').trim();
}

/**
 * Lightweight chat service for Written Preparation.
 * Uses Gemini text model with conversation history.
 */
export class PrepChatService {
  private context: PrepChatContext;
  private history: PrepChatMessage[] = [];
  private examConcept: { term: string; description: string } | null = null;

  constructor(context: PrepChatContext) {
    this.context = context;
  }

  /** Set or clear the exam concept being qualified */
  setExamConcept(concept: { term: string; description: string } | null): void {
    this.examConcept = concept;
  }

  /** Build the full prompt including system context, notes, and conversation history */
  private buildPrompt(userMessage: string, currentNotes: string): string {
    const systemPrompt = this.examConcept
      ? i18n.t('anna:contexts.examQualification')
      : i18n.t('anna:contexts.writtenPreparation');
    const subtopicList = this.context.subtopics.map(s => `- ${s.title}`).join('\n');

    // Recent history (last 10 messages to stay within token limits)
    const recentHistory = this.history.slice(-10);
    const historyStr = recentHistory
      .map(msg => `${msg.role === 'user' ? 'Studenten' : 'Anna'}: ${msg.content}`)
      .join('\n\n');

    let prompt = `${systemPrompt}

EMNE: ${this.context.topicTitle}
UNDEREMNER:
${subtopicList}`;

    if (this.examConcept) {
      prompt += `\n\nBEGREB DER KVALIFICERES:\nTerm: ${this.examConcept.term}\nBeskrivelse: ${this.examConcept.description || '(ingen beskrivelse)'}`;
    }

    prompt += `\n\nELEVENS NUVÆRENDE NOTER:
${currentNotes || '(Ingen noter endnu)'}

SAMTALE HIDTIL:
${historyStr || '(Ny samtale)'}

Studenten: ${userMessage}

Anna:`;

    return prompt;
  }

  /** Send a message and get Anna's response */
  async sendMessage(userMessage: string, currentNotes: string): Promise<PrepChatMessage> {
    // Store user message in history
    const userMsg: PrepChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: userMessage,
      timestamp: new Date(),
    };
    this.history.push(userMsg);

    const prompt = this.buildPrompt(userMessage, currentNotes);

    const text = await callClaudeRoute('/api/prep-chat', { prompt });

    if (!text) {
      throw new Error('Empty response from API');
    }

    // Parse structured tags from Anna's response
    const noteUpdate = parseNoteUpdate(text);
    const conceptQualified = parseConceptQualified(text);

    const assistantMsg: PrepChatMessage = {
      id: `anna-${Date.now()}`,
      role: 'assistant',
      content: text,
      timestamp: new Date(),
      noteUpdate: noteUpdate || undefined,
      conceptQualified: conceptQualified || undefined,
    };
    this.history.push(assistantMsg);

    return assistantMsg;
  }
}
